// FASE 12 — Audit Log centrale Superadmin: ricerca per periodo, utente, ruolo/azione,
// azienda, oggetto. Append-only: questa pagina è sola lettura.
import { useCallback, useEffect, useState } from "react";
import { ScrollText, Search, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY_FILTERS = { date_from: "", date_to: "", user_q: "", action: "", entity: "" };
const PAGE = 50;

export default function PlatformAudit() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [actions, setActions] = useState([]);
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState(null);

  const load = useCallback(async (f = filters, s = skip) => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get("/admin/platform/audit", { params: { ...params, skip: s, limit: PAGE } });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [filters, skip]);

  useEffect(() => {
    api.get("/admin/platform/audit/actions").then((r) => setActions(r.data.actions)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setSkip(0); load(filters, 0); };
  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="platform-audit">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <ScrollText className="w-7 h-7 text-green-800" aria-hidden="true" /> Audit Log
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Registro centrale degli eventi di piattaforma. Append-only: gli eventi non sono
          modificabili né cancellabili da questa console.
        </p>
      </div>

      <Card className="border-slate-200" data-testid="audit-filters">
        <CardContent className="pt-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label htmlFor="af-from" className="text-xs">Dal</Label>
            <Input id="af-from" type="date" value={filters.date_from} onChange={set("date_from")}
              data-testid="audit-filter-from" />
          </div>
          <div>
            <Label htmlFor="af-to" className="text-xs">Al</Label>
            <Input id="af-to" type="date" value={filters.date_to} onChange={set("date_to")}
              data-testid="audit-filter-to" />
          </div>
          <div>
            <Label htmlFor="af-user" className="text-xs">Utente</Label>
            <Input id="af-user" placeholder="email o nome" value={filters.user_q}
              onChange={set("user_q")} data-testid="audit-filter-user" />
          </div>
          <div>
            <Label className="text-xs">Azione</Label>
            <Select value={filters.action || "ALL"}
              onValueChange={(v) => setFilters({ ...filters, action: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutte</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={applyFilters} className="bg-green-800 hover:bg-green-900"
            data-testid="audit-apply-filters">
            <Search className="w-4 h-4 mr-1.5" aria-hidden="true" /> Cerca
          </Button>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-red-700" data-testid="audit-error">{error}</p>}

      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500" data-testid="audit-total">
              {data ? `${data.total} eventi` : "Caricamento…"}
            </p>
            <Button variant="outline" size="sm" className="h-7 text-xs"
              data-testid="audit-export-note"
              onClick={() => window.print()}>
              <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Esporta vista
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="audit-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-semibold">Timestamp</th>
                  <th className="py-2 pr-3 font-semibold">Attore (reale)</th>
                  <th className="py-2 pr-3 font-semibold">Ruolo</th>
                  <th className="py-2 pr-3 font-semibold">Azione</th>
                  <th className="py-2 pr-3 font-semibold">Oggetto</th>
                  <th className="py-2 font-semibold">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((ev) => (
                  <tr key={ev.id} className="border-b border-slate-100 align-top"
                    data-testid={`audit-event-${ev.action}`}>
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                      {new Date(ev.timestamp).toLocaleString("it-IT")}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="font-medium text-slate-800">{ev.user_name || "—"}</span>
                      <span className="block text-[10px] text-slate-500">{ev.user_email}</span>
                    </td>
                    <td className="py-2 pr-3">
                      {ev.user_role && (
                        <Badge variant="outline" className="text-[10px] bg-slate-50">{ev.user_role}</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline"
                        className={`text-[10px] ${String(ev.action).includes("CONTEXT") || String(ev.action).includes("IMPERSONATE") ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-green-50 text-green-800 border-green-300"}`}>
                        {ev.action}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-slate-700">{ev.entity}{ev.entity_id ? ` · ${String(ev.entity_id).slice(0, 8)}…` : ""}</td>
                    <td className="py-2 text-slate-600 max-w-[280px]">
                      <span className="line-clamp-2">{ev.new_value ? JSON.stringify(ev.new_value) : "—"}</span>
                    </td>
                  </tr>
                ))}
                {data && data.items.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-slate-500"
                    data-testid="audit-empty">Nessun evento con questi filtri.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data && data.total > PAGE && (
            <div className="flex items-center gap-2 mt-3">
              <Button variant="outline" size="sm" disabled={skip === 0}
                onClick={() => { const s = Math.max(0, skip - PAGE); setSkip(s); load(filters, s); }}
                data-testid="audit-prev">← Precedenti</Button>
              <Button variant="outline" size="sm" disabled={skip + PAGE >= data.total}
                onClick={() => { const s = skip + PAGE; setSkip(s); load(filters, s); }}
                data-testid="audit-next">Successivi →</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
